import React, { type PropsWithChildren, forwardRef } from 'react';
import classNames from 'classnames';
import { type Tab } from './types';

export type TabRenderContext = 'tabs' | 'menu';

export type FlushTabsProps = {
  activeKey: string;
  className: string;
  id: string;
  renderTab?: (
    tab: Tab,
    context: TabRenderContext
  ) => React.ReactElement | string;
  tabs: Tab[];
  onSelect: Function;
  useLinks?: boolean;
};

const FlushTabsComponent: React.ForwardRefRenderFunction<
  HTMLDivElement,
  PropsWithChildren<FlushTabsProps>
> = (props, ref) => {
  const {
    activeKey,
    children,
    className,
    id,
    renderTab = tab => tab.title,
    tabs,
    onSelect,
    useLinks = false,
  } = props;

  return (
    <div ref={ref} className={classNames('flush-tabs__container', className)}>
      <ul className='flush-tabs__list'>
        {tabs.map((tab, index) => {
          const trackClickData = tab.trackClickData
            ? JSON.stringify(tab.trackClickData)
            : null;

          return (
            <li
              key={tab.key}
              data-tab-key={tab.key}
              id={`${id}-tab-${tab.key}`}
              className={classNames('flush-tabs__tab', {
                'flush-tabs__tab--active': tab.key === activeKey,
                'flush-tabs__tab--hidden': tab.hidden,
              })}
            >
              {useLinks ? (
                <a
                  href={tab.url}
                  className='flush-tabs__button'
                  tabIndex={tab.hidden ? -1 : undefined}
                  data-track-click={trackClickData}
                >
                  {tab.title}
                </a>
              ) : (
                <button
                  className='flush-tabs__button'
                  onClick={() => onSelect(tab)}
                  data-tab-key={tab.key}
                  data-toggle='tab'
                  data-target={`#${id}_tab_${tab.key}`}
                  data-track-click={trackClickData}
                  tabIndex={tab.hidden ? -1 : undefined}
                >
                  {renderTab(tab, 'tabs')}
                </button>
              )}
            </li>
          );
        })}
      </ul>
      {children}
    </div>
  );
};

export const FlushTabs = forwardRef(FlushTabsComponent);
